import type { CoachingGoal } from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import { cacheKeyFor, type Store } from '@warp-drive/core';
import {
  createRecord,
  deleteRecord,
  query,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { jsonApiOptions, page } from '../../json-api.ts';

const MAX_GOALS_PER_PLAN = 100;
type CoachingGoalResourceData = {
  attributes?: Record<string, unknown>;
  [key: string]: unknown;
};

/**
 * Goals on one coaching plan, newest first.
 *
 * ll-api admits the owning coachee and a chemistry/chosen coach for that plan,
 * so this filter is only by plan — a client-side user filter would hide
 * coach-set goals from the coachee, which is the opposite of the point.
 */
export function queryCoachingGoalsByPlan(coachingPlanId: string) {
  return query<CoachingGoal>(
    DataTypeCoaching.CoachingGoal,
    {
      'filter[coachingPlan.id]': coachingPlanId,
      ...page({ offset: 0, limit: MAX_GOALS_PER_PLAN }),
      'sort[]': '-createdAt',
    },
    jsonApiOptions
  );
}

export function createCoachingGoal(coachingGoal: CoachingGoal, store: Store) {
  const init = createRecord(coachingGoal, {
    ...jsonApiOptions,
  });

  const data = store.cache.peek(
    cacheKeyFor(coachingGoal)
  ) as CoachingGoalResourceData;
  init.body = JSON.stringify(coachingGoalCreateDocument(data));

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function coachingGoalCreateDocument(data: CoachingGoalResourceData) {
  return {
    data: {
      ...data,
      attributes: {
        ...data.attributes,
        targetDate: data.attributes?.targetDate || null,
      },
    },
  };
}

/**
 * Sparse PATCH: title, detail, status, targetDate only.
 *
 * ll-api pins `user`, `coachingPlan` and `createdByUserId`. Sending the whole
 * record — the way session-note builders do — 403s when a coach edits a
 * coachee's goal, because the shared handler rejects an update whose `user`
 * is not the caller.
 */
export function updateCoachingGoal(coachingGoal: CoachingGoal) {
  const init = updateRecord(coachingGoal, {
    ...jsonApiOptions,
    patch: true,
  });

  init.body = JSON.stringify(coachingGoalUpdateDocument(coachingGoal));

  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

export function coachingGoalUpdateDocument(
  coachingGoal: Pick<
    CoachingGoal,
    'id' | 'title' | 'detail' | 'status' | 'targetDate'
  >
) {
  return {
    data: {
      type: DataTypeCoaching.CoachingGoal,
      id: coachingGoal.id,
      attributes: {
        title: coachingGoal.title,
        detail: coachingGoal.detail,
        status: coachingGoal.status,
        targetDate: coachingGoal.targetDate || null,
      },
    },
  };
}

export function deleteCoachingGoal(coachingGoal: CoachingGoal) {
  return deleteRecord(coachingGoal, jsonApiOptions);
}
